import React, {useRef, useCallback, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Stack, Typography, Divider} from '@mui/material';

import {formatCardValue} from '../copilot/util';
import DismissSuggestionButton, {
  INCONSISTENCY_DISMISS_REASONS,
} from './DismissSuggestionButton';

const styles = {
  card: {
    unselected: {
      border: '1px solid transparent',
      borderRadius: '8px',
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#F2F5F4',
      },
    },
    selected: {
      borderRadius: '8px',
      border: '1px solid #FF9800',
      boxShadow: '0px 1px 3px 0px rgba(0, 0, 0, 0.15)',
    },
    pill: {
      backgroundColor: '#FFE5E2',
      color: '#962419',
      fontWeight: 500,
      fontSize: '11px',
      borderRadius: '8px',
      padding: '2px 8px',
      width: 'min-content',
      whiteSpace: 'nowrap',
    },
    label: {
      fontSize: '15px',
      fontWeight: 500,
      color: '#162523',
    },
    labelSelected: {
      fontSize: '15px',
      fontWeight: 600,
      color: '#162523',
    },
    itemLabel: {
      color: '#043B34',
      fontSize: '0.875rem',
      fontWeight: 500,
      lineHeight: '160%',
    },
    itemValue: {
      color: '#67716E',
      fontSize: '12px',
      fontWeight: 400,
    },
    locationItem: {
      paddingX: 2,
      paddingY: 1,
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: '#F2F5F4',
      },
    },
    locationItemSelected: {
      paddingX: 2,
      paddingY: 1,
      backgroundColor: '#F2F5F4',
    },
  },
};

const PdfInconsistencyCard = ({
  inconsistency,
  isSelected = false,
  onSelect,
  onLocationSelect,
  documentId,
  onDismiss,
  onDismissError,
}) => {
  const cardRef = useRef(null);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(0);
  const items = inconsistency.items || [];

  const handleCardClick = useCallback(() => {
    if (!isSelected) {
      onSelect?.(inconsistency);
    }
  }, [isSelected, inconsistency, onSelect]);

  const handleLocationClick = useCallback(
    (index, item) => {
      setSelectedLocationIndex(index);
      onLocationSelect?.(inconsistency, item, index);
    },
    [inconsistency, onLocationSelect]
  );

  // Collapsed card view
  if (!isSelected) {
    return (
      <Box ref={cardRef}>
        <Divider sx={{borderColor: '#DCE2E0'}} />
        <Stack
          direction="column"
          py={2}
          px={1}
          onClick={handleCardClick}
          sx={styles.card.unselected}
        >
          <Stack direction="column" gap={1}>
            <Typography sx={styles.card.pill}>Inconsistency issue</Typography>
            <Typography sx={styles.card.label}>
              {inconsistency.title}
            </Typography>
          </Stack>
        </Stack>
      </Box>
    );
  }

  // Expanded card view
  return (
    <Box ref={cardRef}>
      <Stack direction="column" sx={styles.card.selected}>
        <Stack gap={1} p={2}>
          <Typography sx={styles.card.pill}>Inconsistency issue</Typography>
          <Typography sx={styles.card.labelSelected}>
            {inconsistency.title}
          </Typography>
        </Stack>

        <Divider sx={{borderColor: '#E1E5E4'}} />

        {/* Location items */}
        {items.map((item, index) => (
          <React.Fragment key={index}>
            <Stack
              onClick={() => handleLocationClick(index, item)}
              sx={
                selectedLocationIndex === index
                  ? styles.card.locationItemSelected
                  : styles.card.locationItem
              }
            >
              <Typography sx={styles.card.itemLabel}>
                Loc. {index + 1}: {formatCardValue(item.value, item.value_unit)}
              </Typography>
            </Stack>
            <Divider sx={{borderColor: '#E1E5E4'}} />
          </React.Fragment>
        ))}

        <Stack direction="row" paddingY={1} paddingX={2}>
          <DismissSuggestionButton
            documentId={documentId}
            discrepancyId={inconsistency.id}
            checkType="inconsistencies"
            reasons={INCONSISTENCY_DISMISS_REASONS}
            headerText="Dismiss inconsistency? Tell us why:"
            errorMessage="Failed to dismiss inconsistency. Please try again."
            popoverId="pdf-inconsistency-dismiss-popover"
            onDismiss={onDismiss}
            onDismissError={onDismissError}
          />
        </Stack>
      </Stack>
    </Box>
  );
};

PdfInconsistencyCard.propTypes = {
  inconsistency: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    description: PropTypes.string,
    items: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        value_unit: PropTypes.string,
        page_number: PropTypes.number,
        line_item: PropTypes.string,
        column_header: PropTypes.string,
        source: PropTypes.string,
        cell_grounding: PropTypes.shape({
          page: PropTypes.number,
          box: PropTypes.object,
        }),
      })
    ),
  }).isRequired,
  isSelected: PropTypes.bool,
  onSelect: PropTypes.func,
  onLocationSelect: PropTypes.func,
  documentId: PropTypes.string,
  onDismiss: PropTypes.func,
  onDismissError: PropTypes.func,
};

export default PdfInconsistencyCard;
